/* Phase 2.1 admin workspace — organizations editor + invitations manager.
   Guarded by Phase-1 user auth with the `administrator` role.
   Uses the seeded admin creds (marlon@warmhandoffhq.com / whoAdmin2026!) which
   now sign in via /auth/login and carry the `administrator` role via the
   startup mirror in server.py.
*/
import { useState, useEffect, useCallback } from "react";
import { NavLink, Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  LogOut, LayoutDashboard, Building2, Mail, Loader2, Search, Plus, X,
  CheckCircle2, XCircle, AlertTriangle, RotateCcw, ShieldAlert, Trash2, Edit3, Copy,
  Landmark, Upload, Users, Camera,
} from "lucide-react";
import Seo from "@/components/Seo";
import { useUserAuth } from "@/context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const styles = {
  wrap: "min-h-screen bg-cream-panel",
  card: "bg-white border border-borderSubtle rounded-xl shadow-sm",
  input:
    "w-full px-3 py-2.5 bg-white border border-borderSubtle rounded-md text-navy focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 text-sm",
  label: "block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5",
  btnGold:
    "inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-4 py-2.5 rounded-md text-sm disabled:opacity-50 transition-colors",
  btnGhost:
    "inline-flex items-center justify-center gap-2 border border-navy/25 hover:border-navy hover:bg-navy hover:text-cream text-navy font-semibold px-3 py-2 rounded-md text-xs transition-colors",
  btnDanger:
    "inline-flex items-center justify-center gap-2 border border-red-300 hover:bg-red-50 text-red-700 font-semibold px-3 py-2 rounded-md text-xs transition-colors",
  chip: "inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-bold",
};

/* =========================== shared shell =========================== */
export function AdminGate({ children }) {
  const { user, loading } = useUserAuth();
  if (loading) return <div className="min-h-screen bg-cream-panel flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold-deep" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: "/admin/organizations" }} replace />;
  if (!user.roles.includes("administrator")) {
    return (
      <div className="min-h-screen bg-cream-panel flex items-center justify-center p-6">
        <div className={`${styles.card} max-w-md p-8 text-center`} data-testid="admin-forbidden">
          <ShieldAlert className="w-10 h-10 text-red-600 mx-auto" />
          <h1 className="mt-4 font-display font-black text-navy text-2xl">Not authorized</h1>
          <p className="mt-2 text-slateSoft text-sm">This area is restricted to WHO administrators.</p>
          <Link to="/" className={`${styles.btnGhost} mt-6 inline-flex`}>Back to home</Link>
        </div>
      </div>
    );
  }
  return children;
}

export function AdminShell({ title, children, testid }) {
  const { user, signOut } = useUserAuth();
  const nav = useNavigate();
  return (
    <div className={styles.wrap} data-testid={testid}>
      <Seo title={`${title} · WHO Admin`} path="/admin" description="WHO administration" />
      <div className="flex flex-col md:flex-row min-h-screen">
      <aside className="w-full md:w-56 bg-navy-deep text-cream flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <p className="eyebrow text-gold">WHO Admin</p>
          <p className="mt-1 text-xs text-cream/60 truncate">{user?.email}</p>
        </div>
        <nav className="flex-1 py-2 md:py-3 flex flex-row md:flex-col flex-wrap">
          <AdminNav to="/admin" exact label="Dashboard" Icon={LayoutDashboard} />
          <AdminNav to="/admin/organizations" label="Organizations" Icon={Building2} />
          <AdminNav to="/admin/government-resources" label="Government" Icon={Landmark} />
          <AdminNav to="/admin/import" label="CSV Import" Icon={Upload} />
          <AdminNav to="/admin/snapshots" label="Snapshots" Icon={Camera} />
          <AdminNav to="/admin/users/roles" label="Users & Roles" Icon={Users} />
          <AdminNav to="/admin/invitations" label="Invitations" Icon={Mail} />
        </nav>
        <button onClick={async () => { await signOut(); nav("/"); }} className="m-4 inline-flex items-center justify-center gap-2 border border-white/20 hover:border-gold hover:text-gold text-cream/80 text-xs font-semibold px-3 py-2 rounded-md transition-colors" data-testid="admin-signout-btn">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-borderSubtle px-6 lg:px-10 py-5">
          <h1 className="font-display font-black text-navy text-2xl">{title}</h1>
        </header>
        <div className="px-6 lg:px-10 py-8">{children}</div>
      </main>
      </div>
    </div>
  );
}

function AdminNav({ to, label, Icon, exact = false }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-4 md:px-5 py-2.5 text-sm whitespace-nowrap border-b-2 md:border-b-0 md:border-l-4 transition-colors ${
          isActive ? "bg-white/8 text-gold border-gold" : "text-cream/80 hover:bg-white/5 border-transparent"
        }`
      }
      data-testid={`adminnav-${to.split("/").pop() || "dashboard"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}

/* =========================== DASHBOARD =========================== */
export function AdminHome() {
  const { authHeaders } = useUserAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/dashboard/summary`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load summary."));
  }, [authHeaders]);

  return (
    <AdminGate>
      <AdminShell title="Dashboard" testid="admin-dashboard-page">
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="dashboard-error">{err}</p>}
        {!data && !err && <Loader2 className="w-6 h-6 animate-spin text-gold-deep" />}
        {data && (
          <div className="space-y-8">
            <section>
              <h2 className="font-display font-bold text-navy text-lg">Organizations</h2>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-provider-stats">
                <Stat label="Total" value={data.providers.total} />
                <Stat label="Demo" value={data.providers.demo} />
                <Stat label="Live" value={data.providers.live} tone={data.providers.live ? "gold" : undefined} />
                <Stat label="Public published" value={data.providers.public_published} />
                <Stat label="Verified" value={data.providers.verified} />
                <Stat label="Unverified" value={data.providers.unverified} />
                <Stat label="Needs review" value={data.providers.needs_review} />
                <Stat label="Strategic" value={data.providers.strategic} />
                <Stat label="Seeded (WHO-###)" value={data.providers.seeded} />
                <Stat label="Legislative offices" value={data.providers.legislative} />
                <Stat label="Government agencies" value={data.providers.government} />
              </div>
            </section>

            <section>
              <h2 className="font-display font-bold text-navy text-lg">Invitations</h2>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-invitation-stats">
                <Stat label="Pending" value={data.invitations.pending} tone="gold" />
                <Stat label="Accepted" value={data.invitations.redeemed} />
                <Stat label="Expired" value={data.invitations.expired} />
                <Stat label="Revoked" value={data.invitations.revoked} />
              </div>
            </section>

            <section>
              <h2 className="font-display font-bold text-navy text-lg">Recent activity</h2>
              <div className={`${styles.card} mt-3 overflow-hidden`} data-testid="dashboard-audit">
                <table className="w-full text-xs">
                  <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest">
                    <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Actor</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">Resource</th><th className="px-3 py-2 text-left">Result</th></tr>
                  </thead>
                  <tbody>
                    {data.recent_audit.map((e) => (
                      <tr key={e.id} className="border-b border-borderSubtle last:border-0">
                        <td className="px-3 py-2 text-navy whitespace-nowrap">{new Date(e.event_at).toLocaleString()}</td>
                        <td className="px-3 py-2 text-slateSoft">{e.actor_role || "—"}</td>
                        <td className="px-3 py-2 text-navy">{e.action}</td>
                        <td className="px-3 py-2 text-slateSoft">{e.resource_type || "—"}</td>
                        <td className="px-3 py-2"><span className={`${styles.chip} ${e.result === "ok" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>{e.result}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </AdminShell>
    </AdminGate>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`${styles.card} p-4`}>
      <p className="text-xs uppercase tracking-widest text-navy/60 font-semibold">{label}</p>
      <p className={`mt-2 font-display font-black text-3xl ${tone === "gold" ? "text-gold-deep" : "text-navy"}`}>{value}</p>
    </div>
  );
}

/* =========================== ORGANIZATIONS =========================== */
export function AdminOrganizations() {
  const { authHeaders } = useUserAuth();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ q: "", visibility: "all" });
  const [editing, setEditing] = useState(null); // provider or {new:true}
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.visibility !== "all") params.visibility = filter.visibility;
    const r = await axios.get(`${API}/admin/directory/providers`, { headers: authHeaders(), params });
    setList(r.data.providers || []);
  }, [authHeaders, filter]);

  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(""), 2500); };

  const save = async (payload) => {
    if (editing?.new) {
      await axios.post(`${API}/admin/directory/providers`, payload, { headers: authHeaders() });
      flash("Organization created.");
    } else {
      await axios.patch(`${API}/admin/directory/providers/${editing.id}`, payload, { headers: authHeaders() });
      flash("Changes saved.");
    }
    setEditing(null);
    load();
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this organization? It will be hidden from Discovery.")) return;
    await axios.delete(`${API}/admin/directory/providers/${id}`, { headers: authHeaders() });
    flash("Organization archived.");
    load();
  };

  const activate = async (p) => {
    const note = window.prompt(`Activate ${p.display_name} for LIVE referrals? Enter approval note (5+ characters):`);
    if (!note || note.length < 5) return;
    try {
      await axios.post(`${API}/admin/directory/providers/${p.id}/activate`, { approval_note: note }, { headers: authHeaders() });
      flash("Provider activated for live referrals.");
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Activation blocked.");
    }
  };

  return (
    <AdminGate>
      <AdminShell title="Organizations" testid="admin-orgs-page">
        {status && <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="orgs-flash">{status}</p>}

        <div className="flex flex-wrap gap-3 items-end mb-6">
          <div className="flex-1 min-w-[220px]">
            <label className={styles.label}>Search</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute top-3 left-3 text-slateSoft pointer-events-none" />
              <input className={`${styles.input} pl-9`} value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Name, legal name, tag" data-testid="orgs-search" />
            </div>
          </div>
          <div>
            <label className={styles.label}>Visibility</label>
            <select className={styles.input} value={filter.visibility} onChange={(e) => setFilter({ ...filter, visibility: e.target.value })} data-testid="orgs-visibility-filter">
              <option value="all">All</option>
              <option value="public">Public</option>
              <option value="authenticated">Authenticated</option>
              <option value="admin_only">Admin only</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
          <button className={styles.btnGold} onClick={() => setEditing({ new: true })} data-testid="orgs-create-btn"><Plus className="w-4 h-4" /> New organization</button>
        </div>

        <div className={`${styles.card} overflow-x-auto`} data-testid="orgs-table">
          <table className="w-full text-sm">
            <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest text-xs">
              <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Categories</th><th className="px-3 py-2 text-left">Partnership</th><th className="px-3 py-2 text-left">Flags</th><th className="px-3 py-2 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b border-borderSubtle last:border-0 hover:bg-cream" data-testid={`org-row-${p.id}`}>
                  <td className="px-3 py-3 text-navy">
                    <p className="font-semibold">{p.display_name}</p>
                    <p className="text-xs text-slateSoft truncate max-w-[260px]">{p.legal_name}</p>
                  </td>
                  <td className="px-3 py-3 text-slateSoft text-xs">{(p.categories || []).join(", ").replace(/_/g, " ")}</td>
                  <td className="px-3 py-3 text-xs">
                    <PartnershipBadge value={p.partnership_status} />
                  </td>
                  <td className="px-3 py-3 text-xs space-x-1">
                    {p.demo && <span className={`${styles.chip} bg-gold/15 text-gold-deep border border-gold/40`}>Demo</span>}
                    {!p.demo && <span className={`${styles.chip} bg-green-50 text-green-800 border border-green-200`}>Live-eligible</span>}
                    {p.live_referrals_enabled && <span className={`${styles.chip} bg-navy text-cream`}>Live referrals ON</span>}
                    {p.visibility === "admin_only" && <span className={`${styles.chip} bg-navy/10 text-navy border border-navy/20`}>Admin only</span>}
                    {p.organization_role === "legislative_office" && <span className={`${styles.chip} bg-sky-50 text-sky-800 border border-sky-200`}>Legislative · Discovery only</span>}
                    {p.verification_status === "verified" && <span className={`${styles.chip} bg-green-50 text-green-800 border border-green-200`}>Verified</span>}
                    {!p.published && <span className={`${styles.chip} bg-red-50 text-red-800 border border-red-200`}>Hidden</span>}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap space-x-1">
                    <button className={styles.btnGhost} onClick={() => setEditing(p)} data-testid={`org-edit-${p.id}`}><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                    {!p.live_referrals_enabled && (
                      <button className={styles.btnGhost} onClick={() => activate(p)} data-testid={`org-activate-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Activate</button>
                    )}
                    <button className={styles.btnDanger} onClick={() => archive(p.id)} data-testid={`org-archive-${p.id}`}><Trash2 className="w-3.5 h-3.5" /> Archive</button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slateSoft">No organizations match.</td></tr>}
            </tbody>
          </table>
        </div>

        {editing && <OrgEditDialog editing={editing} onClose={() => setEditing(null)} onSave={save} />}
      </AdminShell>
    </AdminGate>
  );
}

function PartnershipBadge({ value }) {
  const map = {
    unconfirmed: ["bg-navy/5 text-navy/70 border border-navy/15", "Unconfirmed"],
    in_discussion: ["bg-gold/15 text-gold-deep border border-gold/40", "In discussion"],
    confirmed: ["bg-green-50 text-green-800 border border-green-200", "Confirmed"],
    declined: ["bg-red-50 text-red-700 border border-red-200", "Declined"],
  };
  const [cls, label] = map[value] || map.unconfirmed;
  return <span className={`${styles.chip} ${cls}`}>{label}</span>;
}

const CATEGORIES = [
  "healthcare", "behavioral_health", "housing", "employment", "education", "benefits", "legal",
  "financial", "transportation", "family_support", "peer_support", "faith_community", "emergency",
  "government_federal", "government_state", "government_local", "legislative", "philanthropy", "strategic_partner",
];

function OrgEditDialog({ editing, onClose, onSave }) {
  const isNew = !!editing.new;
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState(() => ({
    legal_name: editing.legal_name || "",
    display_name: editing.display_name || "",
    categories: editing.categories || [],
    service_areas: (editing.service_areas || []).join(", "),
    description: editing.description || "",
    website: editing.website || "",
    phone: editing.phone || "",
    email: editing.email || "",
    address: editing.address || "",
    tags: (editing.tags || []).join(", "),
    visibility: editing.visibility || "public",
    published: editing.published ?? true,
    demo: editing.demo ?? true,
    partnership_status: editing.partnership_status || "unconfirmed",
    organization_role: editing.organization_role || "direct_service",
    internal_notes: editing.internal_notes || "",
    change_reason: "",
  }));
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = {
      ...form,
      service_areas: form.service_areas.split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    // On edit, strip unchanged optional empties so PATCH stays small
    if (!isNew) {
      Object.keys(payload).forEach((k) => { if (payload[k] === "" && k !== "internal_notes") delete payload[k]; });
    }
    try {
      await onSave(payload);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed.");
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-start justify-center p-6 overflow-y-auto" data-testid="org-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 lg:p-8 space-y-4 my-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold-deep">{isNew ? "New organization" : "Edit organization"}</p>
            <h2 className="mt-1 font-display font-black text-navy text-xl">{editing.display_name || "Untitled"}</h2>
            {editing.seed_id && <p className="mt-0.5 text-xs text-slateSoft">Seed record {editing.seed_id} · verification: {editing.verification_status || "unverified"}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>

        {!isNew && (
          <div className="flex gap-1 border-b border-borderSubtle" data-testid="org-dialog-tabs">
            {[["details", "Details"], ["history", "Change History"]].map(([key, label]) => (
              <button
                type="button" key={key} onClick={() => setTab(key)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === key ? "border-gold text-navy" : "border-transparent text-slateSoft hover:text-navy"}`}
                data-testid={`org-tab-${key}`}
              >{label}</button>
            ))}
          </div>
        )}

        {tab === "history" && !isNew ? (
          <OrgHistory providerId={editing.id} />
        ) : (
        <>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="org-dialog-error">{err}</p>}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Display name"><input className={styles.input} required value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="org-display-name" /></Field>
          <Field label="Legal name"><input className={styles.input} required value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="org-legal-name" /></Field>
          <Field label="Website"><input className={styles.input} value={form.website} onChange={(e) => set("website", e.target.value)} /></Field>
          <Field label="Phone"><input className={styles.input} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Email"><input className={styles.input} value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Address"><input className={styles.input} value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
        </div>

        <Field label="Service areas (comma separated)">
          <input className={styles.input} value={form.service_areas} onChange={(e) => set("service_areas", e.target.value)} placeholder="Allegheny County, Western PA" />
        </Field>

        <Field label="Tags (comma separated)">
          <input className={styles.input} value={form.tags} onChange={(e) => set("tags", e.target.value)} />
        </Field>

        <Field label="Categories">
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="org-categories">
            {CATEGORIES.map((c) => {
              const on = form.categories.includes(c);
              return (
                <button type="button" key={c} onClick={() => set("categories", on ? form.categories.filter((x) => x !== c) : [...form.categories, c])} className={`text-xs rounded-full border px-2.5 py-1 font-semibold transition-colors ${on ? "bg-navy text-cream border-navy" : "bg-white text-navy border-borderSubtle hover:border-navy/40"}`} data-testid={`org-cat-${c}`}>
                  {c.replace(/_/g, " ")}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Description"><textarea className={`${styles.input} min-h-[80px]`} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Visibility">
            <select className={styles.input} value={form.visibility} onChange={(e) => set("visibility", e.target.value)} data-testid="org-visibility">
              {["public", "authenticated", "admin_only", "hidden"].map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Partnership status">
            <select className={styles.input} value={form.partnership_status} onChange={(e) => set("partnership_status", e.target.value)} data-testid="org-partnership">
              {["unconfirmed", "in_discussion", "confirmed", "declined"].map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Published">
            <select className={styles.input} value={form.published ? "yes" : "no"} onChange={(e) => set("published", e.target.value === "yes")} data-testid="org-published">
              <option value="yes">Yes</option><option value="no">No</option>
            </select>
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Organization role">
            <select className={styles.input} value={form.organization_role} onChange={(e) => set("organization_role", e.target.value)} data-testid="org-role">
              <option value="direct_service">Direct service</option>
              <option value="strategic_ecosystem">Strategic ecosystem</option>
              <option value="government_agency">Government agency</option>
              <option value="legislative_office">Legislative office</option>
            </select>
          </Field>
          {form.organization_role === "legislative_office" && (
            <p className="self-end text-xs text-navy/70 bg-navy/5 border border-navy/15 rounded-md px-3 py-2" data-testid="org-legislative-note">
              Legislative offices stay <strong>Discovery Only</strong> — they cannot be activated for referrals during the pilot.
            </p>
          )}
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="org-dialog-safety">
          <p className="flex items-center gap-2 text-sm text-amber-900 font-semibold"><AlertTriangle className="w-4 h-4" /> Live referral activation is intentionally a separate action.</p>
          <p className="mt-1 text-xs text-amber-900/80">To enable this organization for live warm handoffs, close this dialog and use the <strong>Activate</strong> button in the table — an approval note will be required and audit-logged. Demo records cannot be activated.</p>
          <div className="mt-3 flex gap-4 text-xs text-amber-900">
            <label className="flex items-center gap-2"><input type="checkbox" className="accent-amber-700" checked={!form.demo} onChange={(e) => set("demo", !e.target.checked)} data-testid="org-live-eligible" />Mark as live-eligible (demo = off)</label>
          </div>
        </div>

        <Field label="Internal notes (admins only)"><textarea className={`${styles.input} min-h-[60px]`} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} data-testid="org-internal-notes" /></Field>

        <Field label="Reason for change (recorded in the audit log)">
          <input className={styles.input} value={form.change_reason} onChange={(e) => set("change_reason", e.target.value)} placeholder="e.g. Verified phone number against official site" data-testid="org-change-reason" />
        </Field>

        <div className="flex justify-end gap-3 pt-2 border-t border-borderSubtle">
          <button type="button" onClick={onClose} className={styles.btnGhost}>Cancel</button>
          <button type="submit" className={styles.btnGold} data-testid="org-save">{isNew ? "Create" : "Save changes"}</button>
        </div>
        </>
        )}
      </form>
    </div>
  );
}

/* ---- Change History tab: sourced from the append-only audit log ---- */
function OrgHistory({ providerId }) {
  const { authHeaders } = useUserAuth();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/directory/providers/${providerId}/history`, { headers: authHeaders() })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, [providerId, authHeaders]);

  if (!events) return <Loader2 className="w-5 h-5 animate-spin text-gold-deep" />;
  if (events.length === 0) return <p className="text-sm text-slateSoft py-6 text-center" data-testid="org-history-empty">No recorded changes yet.</p>;

  return (
    <ol className="relative border-l border-borderSubtle ml-2 space-y-5 py-2 max-h-[420px] overflow-y-auto pr-2" data-testid="org-history">
      {events.map((e) => (
        <li key={e.id} className="ml-4">
          <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-gold border-2 border-white" />
          <div className="flex flex-wrap items-center gap-2 text-xs text-slateSoft">
            <span className="font-semibold text-navy">{new Date(e.event_at).toLocaleString()}</span>
            <span className={`${styles.chip} bg-navy/5 text-navy border border-navy/15`}>{e.action}</span>
            {e.actor_role && <span>by {e.actor_role}</span>}
            {e.result !== "ok" && <span className={`${styles.chip} bg-red-50 text-red-800 border border-red-200`}>{e.result}</span>}
          </div>
          {(e.meta?.changes || []).length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs">
              {e.meta.changes.map((c, i) => (
                <li key={i} className="text-slateSoft">
                  <span className="font-semibold text-navy">{c.field.replace(/_/g, " ")}</span>
                  {": "}{c.old ?? "—"} <span className="text-gold-deep">→</span> {c.new ?? "—"}
                </li>
              ))}
            </ul>
          )}
          {e.meta?.reason && <p className="mt-1 text-xs italic text-slateSoft">Reason: {e.meta.reason}</p>}
          {e.meta?.source && <p className="mt-1 text-xs text-slateSoft">Source: {e.meta.source}</p>}
          {e.meta?.approval_note && <p className="mt-1 text-xs italic text-slateSoft">Approval: {e.meta.approval_note}</p>}
        </li>
      ))}
    </ol>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}

/* =========================== INVITATIONS =========================== */
export function AdminInvitations() {
  const { authHeaders } = useUserAuth();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [newToken, setNewToken] = useState(null); // {token, url, invite}
  const [status, setStatus] = useState("");
  const [providers, setProviders] = useState([]);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/invitations`, { headers: authHeaders(), params: filter ? { status: filter } : {} });
    setList(r.data.invitations || []);
  }, [authHeaders, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/admin/directory/providers`, { headers: authHeaders(), params: { limit: 500 } })
      .then((r) => setProviders(r.data.providers || []));
  }, [authHeaders]);

  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(""), 2500); };

  const issue = async (payload) => {
    const r = await axios.post(`${API}/admin/invitations`, payload, { headers: authHeaders() });
    if (r.data.invitation.account_creating) {
      const url = `${window.location.origin}/invite/accept?token=${encodeURIComponent(r.data.token)}`;
      setNewToken({ token: r.data.token, url, invite: r.data.invitation });
    } else {
      setNewToken({ token: null, url: null, invite: r.data.invitation });
    }
    setIssuing(false);
    load();
    flash(r.data.invitation.account_creating
      ? "Invitation issued. Copy the link now — it won't be shown again."
      : "Outreach recorded. No account link is generated for this classification.");
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invitation? The current link will stop working.")) return;
    await axios.post(`${API}/admin/invitations/${id}/revoke`, {}, { headers: authHeaders() });
    load();
    flash("Invitation revoked.");
  };

  const resend = async (id) => {
    const r = await axios.post(`${API}/admin/invitations/${id}/resend`, {}, { headers: authHeaders() });
    if (r.data.invitation.account_creating) {
      const url = `${window.location.origin}/invite/accept?token=${encodeURIComponent(r.data.token)}`;
      setNewToken({ token: r.data.token, url, invite: r.data.invitation });
    }
    load();
    flash("Invitation reissued with a fresh 72h validity window.");
  };

  return (
    <AdminGate>
      <AdminShell title="Invitations" testid="admin-invitations-page">
        {status && <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="inv-flash">{status}</p>}

        <div className="flex items-end gap-3 mb-6">
          <div>
            <label className={styles.label}>Filter</label>
            <select className={styles.input} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="inv-filter">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="redeemed">Redeemed</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
          <button className={styles.btnGold} onClick={() => setIssuing(true)} data-testid="inv-new-btn"><Plus className="w-4 h-4" /> New invitation</button>
        </div>

        <div className={`${styles.card} overflow-x-auto`} data-testid="inv-table">
          <table className="w-full text-sm">
            <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest text-xs">
              <tr><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Classification</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Expires</th><th className="px-3 py-2 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.id} className="border-b border-borderSubtle last:border-0" data-testid={`inv-row-${i.id}`}>
                  <td className="px-3 py-3 text-navy">{i.email}</td>
                  <td className="px-3 py-3"><ClassificationBadge value={i.classification} /></td>
                  <td className="px-3 py-3 text-slateSoft capitalize">{i.role ? i.role.replace(/_/g, " ") : "—"}</td>
                  <td className="px-3 py-3"><InvStatusBadge value={i.status} /></td>
                  <td className="px-3 py-3 text-xs text-slateSoft">{new Date(i.expires_at).toLocaleString()}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap space-x-1">
                    {i.status === "pending" && <button className={styles.btnGhost} onClick={() => revoke(i.id)} data-testid={`inv-revoke-${i.id}`}><XCircle className="w-3.5 h-3.5" /> Revoke</button>}
                    {i.status !== "redeemed" && <button className={styles.btnGhost} onClick={() => resend(i.id)} data-testid={`inv-resend-${i.id}`}><RotateCcw className="w-3.5 h-3.5" /> Resend</button>}
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slateSoft">No invitations.</td></tr>}
            </tbody>
          </table>
        </div>

        {issuing && <InvDialog providers={providers} onClose={() => setIssuing(false)} onIssue={issue} />}
        {newToken && <NewTokenDialog data={newToken} onClose={() => setNewToken(null)} />}
      </AdminShell>
    </AdminGate>
  );
}

function InvStatusBadge({ value }) {
  const map = {
    pending: ["bg-gold/15 text-gold-deep border border-gold/40", "Pending"],
    redeemed: ["bg-green-50 text-green-800 border border-green-200", "Redeemed"],
    expired: ["bg-navy/5 text-navy/60 border border-navy/15", "Expired"],
    revoked: ["bg-red-50 text-red-700 border border-red-200", "Revoked"],
  };
  const [cls, label] = map[value] || map.pending;
  return <span className={`${styles.chip} ${cls}`}>{label}</span>;
}

function ClassificationBadge({ value }) {
  const map = {
    veteran_pilot: ["bg-gold/15 text-gold-deep border border-gold/40", "Veteran pilot"],
    who_guide_invitation: ["bg-navy/5 text-navy border border-navy/20", "WHO Guide"],
    provider_pilot: ["bg-green-50 text-green-800 border border-green-200", "Provider pilot"],
    strategic_outreach: ["bg-purple-50 text-purple-800 border border-purple-200", "Strategic outreach"],
    government_relations: ["bg-sky-50 text-sky-800 border border-sky-200", "Gov relations"],
  };
  const [cls, label] = map[value] || ["bg-navy/5 text-navy/60 border border-navy/15", (value || "—").replace(/_/g, " ")];
  return <span className={`${styles.chip} ${cls}`}>{label}</span>;
}

const CLASSIFICATIONS = [
  { value: "veteran_pilot", label: "Veteran Pilot Invitation", roles: ["veteran", "family"], creates: true },
  { value: "who_guide_invitation", label: "WHO Guide Invitation", roles: ["who_guide", "administrator"], creates: true },
  { value: "provider_pilot", label: "Provider Pilot Invitation", roles: ["provider_user"], creates: true },
  { value: "strategic_outreach", label: "Strategic Partner Outreach (no account)", roles: [], creates: false },
  { value: "government_relations", label: "Government Relations Outreach (no account)", roles: [], creates: false },
];

function InvDialog({ providers, onClose, onIssue }) {
  const [form, setForm] = useState({ email: "", classification: "veteran_pilot", role: "veteran", provider_id: "", display_name: "", note: "", expires_in_hours: 72 });
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const cls = CLASSIFICATIONS.find((c) => c.value === form.classification);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = { email: form.email, classification: form.classification, expires_in_hours: form.expires_in_hours };
    if (cls.creates) payload.role = form.role;
    if (form.role === "provider_user" && cls.creates) payload.provider_id = form.provider_id;
    if (form.display_name) payload.display_name = form.display_name;
    if (form.note) payload.note = form.note;
    try {
      await onIssue(payload);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not issue invitation.");
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-start justify-center p-6 overflow-y-auto" data-testid="inv-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 lg:p-8 space-y-4 my-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold-deep">New invitation</p>
            <h2 className="mt-1 font-display font-black text-navy text-xl">Invite someone to the WHO pilot</h2>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="inv-dialog-error">{err}</p>}
        <Field label="Classification">
          <select className={styles.input} value={form.classification} onChange={(e) => {
            const next = CLASSIFICATIONS.find((c) => c.value === e.target.value);
            setForm((s) => ({ ...s, classification: next.value, role: next.roles[0] || "" }));
          }} data-testid="inv-classification">
            {CLASSIFICATIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        {!cls.creates && (
          <p className="text-xs text-navy/70 bg-navy/5 border border-navy/15 rounded-md px-3 py-2" data-testid="inv-outreach-note">
            Outreach records are tracked for relationship management only. They <strong>never create platform accounts</strong> and never activate referrals. No redeemable link is generated.
          </p>
        )}
        <Field label="Email"><input type="email" required className={styles.input} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="inv-email" /></Field>
        {cls.creates && (
          <Field label="Role">
            <select className={styles.input} value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="inv-role">
              {cls.roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
        )}
        {cls.creates && form.role === "provider_user" && (
          <Field label="Provider">
            <select className={styles.input} required value={form.provider_id} onChange={(e) => set("provider_id", e.target.value)} data-testid="inv-provider">
              <option value="">— Select provider —</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Name (optional)"><input className={styles.input} value={form.display_name} onChange={(e) => set("display_name", e.target.value)} /></Field>
        <Field label="Internal note (optional)"><textarea className={`${styles.input} min-h-[60px]`} value={form.note} onChange={(e) => set("note", e.target.value)} /></Field>
        <Field label="Expires in (hours)"><input type="number" min={1} max={720} className={styles.input} value={form.expires_in_hours} onChange={(e) => set("expires_in_hours", Number(e.target.value))} data-testid="inv-expires" /></Field>
        <div className="flex justify-end gap-3 pt-2 border-t border-borderSubtle">
          <button type="button" onClick={onClose} className={styles.btnGhost}>Cancel</button>
          <button type="submit" className={styles.btnGold} data-testid="inv-issue">{cls.creates ? "Issue invitation" : "Record outreach"}</button>
        </div>
      </form>
    </div>
  );
}

function NewTokenDialog({ data, onClose }) {
  const copy = async () => {
    await navigator.clipboard.writeText(data.url);
  };
  if (!data.token) {
    return (
      <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="new-token-dialog">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 lg:p-8 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="eyebrow text-gold-deep">Outreach recorded</p>
              <h2 className="mt-1 font-display font-black text-navy text-xl">No account link generated</h2>
              <p className="mt-1 text-xs text-slateSoft"><strong>{data.invite.email}</strong> was recorded as {(data.invite.classification || "").replace(/_/g, " ")}. Outreach classifications never create platform accounts or activate referrals — contact happens entirely out-of-band.</p>
            </div>
            <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className={styles.btnGold} data-testid="outreach-done">Done</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="new-token-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 lg:p-8 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold-deep">Invitation issued</p>
            <h2 className="mt-1 font-display font-black text-navy text-xl">Copy this link now</h2>
            <p className="mt-1 text-xs text-slateSoft">This link is shown only once. The server stores a one-way hash of the token. Deliver this link to <strong>{data.invite.email}</strong> out-of-band (email, SMS, courier).</p>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="rounded-md bg-cream-panel border border-borderSubtle p-3 text-xs break-all font-mono text-navy" data-testid="new-token-url">{data.url}</div>
        <div className="flex gap-3">
          <button type="button" onClick={copy} className={styles.btnGold} data-testid="new-token-copy"><Copy className="w-4 h-4" /> Copy link</button>
          <button type="button" onClick={onClose} className={styles.btnGhost}>Done</button>
        </div>
      </div>
    </div>
  );
}
